/**
 * Scanning is deliberately split behind one interface with two implementations, because the
 * browser cannot be assumed: `BarcodeDetector` is Chromium-only, so Firefox on Android and
 * some in-app WebViews need the WASM decoder. The screens never know which one is live.
 *
 * Feature detection, never user-agent sniffing — and the native API is only accepted if it
 * actually advertises QR support, since some builds expose the constructor without it.
 */

export interface ScanEngine {
  readonly name: 'native' | 'wasm';
  /** Begin decoding frames from `video`. Resolves once running. */
  start(video: HTMLVideoElement, onResult: (text: string) => void): Promise<void>;
  stop(): void;
}

export async function nativeAvailable(): Promise<boolean> {
  const Ctor = (globalThis as any).BarcodeDetector;
  if (!Ctor) return false;
  try {
    const formats: string[] = await Ctor.getSupportedFormats();
    return formats.includes('qr_code');
  } catch {
    return false;
  }
}

export async function pickEngine(forceFallback = false): Promise<ScanEngine> {
  if (!forceFallback && (await nativeAvailable())) {
    const { NativeEngine } = await import('./native.ts');
    return new NativeEngine();
  }
  // Loaded on demand, so a Chromium browser never downloads the WASM binary at all.
  const { WasmEngine } = await import('./zxing.ts');
  return new WasmEngine();
}

/** Rear camera at a resolution high enough to resolve a label from arm's length. */
export async function openCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    audio: false,
  });
}

export function closeCamera(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}

/** Frame grabber shared by both engines. */
export class FrameGrabber {
  private canvas = document.createElement('canvas');
  private ctx = this.canvas.getContext('2d', { willReadFrequently: true });

  grab(video: HTMLVideoElement): ImageData | null {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h || !this.ctx) return null;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.ctx.drawImage(video, 0, 0, w, h);
    return this.ctx.getImageData(0, 0, w, h);
  }
}
